import { Controller } from "@hotwired/stimulus"

// Apryse WebViewer integration for read-only PDF viewing with annotations.
// Works with the modal_controller.js pattern (data-modal-open="pdf-viewer-modal").
export default class extends Controller {
  static targets = ["viewerContainer", "title", "loading", "filename"]
  static values = { licenseKey: String, viewerDataUrl: String, scrollZoom: { type: Boolean, default: true }, scrollZoomContext: { type: String, default: "design" } }

  connect() {
    this._instance = null
    this._currentDevPdfId = null
    this._onGlobalClick = this._onGlobalClick.bind(this)
    document.addEventListener("click", this._onGlobalClick)
  }

  // Intercept clicks on [data-modal-open="pdf-viewer-modal"] that also have data-dev-pdf-id
  _onGlobalClick(event) {
    const trigger = event.target.closest('[data-modal-open="pdf-viewer-modal"]')
    if (!trigger) return

    const devPdfId = trigger.dataset.devPdfId
    if (!devPdfId) return

    // Defer to allow modal_controller to open the modal first
    requestAnimationFrame(() => this._loadPdf(devPdfId))
  }

  async _loadPdf(devPdfId) {
    if (this._currentDevPdfId === devPdfId && this._instance) return

    this._currentDevPdfId = devPdfId
    this.loadingTarget.classList.remove("hidden")
    this.titleTarget.textContent = "Loading..."
    this.filenameTarget.textContent = ""

    try {
      const response = await fetch(this.viewerDataUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ dev_pdf_id: devPdfId })
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()

      this.titleTarget.textContent = data.name || "PDF Viewer"
      this.filenameTarget.textContent = data.name || ""

      if (!this._instance) {
        await this._initWebViewer(data)
      } else {
        await this._loadDocument(data)
      }

      // Sync Apryse theme with app theme on every open
      const theme = document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light"
      this._instance.UI.setTheme(theme)
    } catch (error) {
      console.error("Failed to load PDF:", error)
      this.titleTarget.textContent = "Error loading PDF"
      this.loadingTarget.classList.add("hidden")
    }
  }

  async _initWebViewer(data) {
    const instance = await window.WebViewer({
      path: "/webviewer",
      licenseKey: this.licenseKeyValue,
      fullAPI: true
    }, this.viewerContainerTarget)

    this._instance = instance

    // Disable annotation editing UI elements
    instance.UI.disableElements([
      "toolsHeader",
      "annotationPopup",
      "contextMenuPopup"
    ])

    await this._loadDocument(data)
    this._setupScrollZoom()
    this._addScrollZoomToggle()
  }

  _setupScrollZoom() {
    this._scrollZoomEnabled = this.scrollZoomValue
    const { Core } = this._instance
    const docViewer = Core.documentViewer
    const scrollViewElement = docViewer.getScrollViewElement()
    const viewerElement = docViewer.getViewerElement()

    // Bluebeam-style wheel zoom — same gesture-locked algorithm as
    // action_item_pdf_viewer_controller (validated there with a measurement
    // harness: 0px anchor error at page corners/center; gutter spins pin the
    // nearest page edge; per-frame batching cuts tile-repaint flicker).
    let anchor = null, pending = 0, scheduled = false
    const flush = () => {
      scheduled = false
      const dY = pending; pending = 0
      if (!dY || !anchor) return
      const cur = docViewer.getZoomLevel()
      const factor = Math.min(1.6, Math.max(0.62, Math.exp(-dY * 0.0015)))
      const z = Math.max(0.25, Math.min(16.0, cur * factor))
      if (Math.abs(z - cur) < 0.0005) return
      docViewer.zoomTo(z)
      if (scrollViewElement && viewerElement) {
        const vr = viewerElement.getBoundingClientRect()
        scrollViewElement.scrollLeft += Math.round(vr.left + anchor.docX * z - anchor.mx)
        scrollViewElement.scrollTop += Math.round(vr.top + anchor.docY * z - anchor.my)
      }
      // Keep the whole page painted at moderate zoom so zooming out scales
      // the existing image instead of flashing white around the previously
      // visible rectangle — see action_item_pdf_viewer_controller.
      try {
        if (typeof docViewer.isInViewportRenderMode === "function") {
          const wantViewport = z > 3
          if (docViewer.isInViewportRenderMode() !== wantViewport) {
            if (wantViewport) docViewer.enableViewportRenderMode()
            else docViewer.disableViewportRenderMode()
          }
        }
      } catch (_) { /* engine build without the API */ }
    }
    this._wheelHandler = (e) => {
      if (!this._scrollZoomEnabled) return
      e.preventDefault()
      e.stopPropagation()
      const now = performance.now()
      if (viewerElement && (!anchor || now - anchor.t > 300 ||
          Math.abs(e.clientX - anchor.mx) > 3 || Math.abs(e.clientY - anchor.my) > 3)) {
        const zz = docViewer.getZoomLevel()
        const vr = viewerElement.getBoundingClientRect()
        anchor = {
          mx: e.clientX, my: e.clientY, t: now,
          docX: Math.min(Math.max((e.clientX - vr.left) / zz, 0), vr.width / zz),
          docY: Math.min(Math.max((e.clientY - vr.top) / zz, 0), vr.height / zz)
        }
      } else if (anchor) { anchor.t = now }
      pending += e.deltaY
      if (!scheduled) {
        scheduled = true
        if (typeof requestAnimationFrame === "function") requestAnimationFrame(flush)
        else setTimeout(flush, 16)
      }
    }

    // Attach to the SCROLL AREA so spins over the gray gutter zoom too.
    const target = scrollViewElement || viewerElement
    target.addEventListener('wheel', this._wheelHandler, { passive: false })
  }

  _addScrollZoomToggle() {
    const { UI } = this._instance
    const self = this

    try {
      const topHeader = UI.getModularHeader('default-top-header')
      if (!topHeader) return

      const leftGroup = topHeader.items.find(i => i.dataElement === 'groupedLeftHeaderButtons')
      if (!leftGroup || !leftGroup.items) return

      const toggleBtn = new UI.Components.CustomButton({
        dataElement: 'scrollZoomToggle',
        title: self._scrollZoomEnabled ? 'Scroll to Zoom: ON' : 'Scroll to Zoom: OFF',
        img: self._scrollZoomSvg(self._scrollZoomEnabled),
        onClick: () => self._toggleScrollZoom()
      })

      const items = [...leftGroup.items]
      const zoomIdx = items.findIndex(i => i.dataElement === 'zoom-container')
      items.splice(zoomIdx !== -1 ? zoomIdx + 1 : items.length, 0, toggleBtn)
      leftGroup.setItems(items)
      setTimeout(() => this._applyToggleStyling(), 500)
    } catch (e) {
      console.warn('[scroll-zoom] Could not add toggle:', e)
    }
  }

  _toggleScrollZoom() {
    this._scrollZoomEnabled = !this._scrollZoomEnabled
    this.scrollZoomValue = this._scrollZoomEnabled

    try {
      const { UI } = this._instance
      const topHeader = UI.getModularHeader('default-top-header')
      const leftGroup = topHeader?.items.find(i => i.dataElement === 'groupedLeftHeaderButtons')
      if (leftGroup) {
        const items = [...leftGroup.items]
        const idx = items.findIndex(i => i.dataElement === 'scrollZoomToggle')
        if (idx !== -1) {
          const self = this
          items[idx] = new UI.Components.CustomButton({
            dataElement: 'scrollZoomToggle',
            title: self._scrollZoomEnabled ? 'Scroll to Zoom: ON' : 'Scroll to Zoom: OFF',
            img: self._scrollZoomSvg(self._scrollZoomEnabled),
            onClick: () => self._toggleScrollZoom()
          })
          leftGroup.setItems(items)
          setTimeout(() => this._applyToggleStyling(), 300)
        }
      }
    } catch (_) {}

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) {
      fetch('/save_pdf_scroll_zoom', {
        method: 'POST',
        headers: { 'X-CSRF-Token': token, 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `enabled=${this._scrollZoomEnabled}&context=${this.scrollZoomContextValue}`
      })
    }
  }

  _applyToggleStyling() {
    if (!this._instance) return
    try {
      const iframeDoc = this._instance.UI.iframeWindow.document
      const btn = iframeDoc.querySelector('[data-element="scrollZoomToggle"]')
      if (btn) {
        const on = this._scrollZoomEnabled
        btn.title = on ? 'Scroll to Zoom: ON' : 'Scroll to Zoom: OFF'
        btn.style.background = on ? 'rgba(56,132,244,0.15)' : ''
        btn.style.borderRadius = '4px'
      }
    } catch (_) {}
  }

  _scrollZoomSvg(active) {
    const color = active ? '%233884f4' : '%23868e96'
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="6" y="3" width="12" height="18" rx="6"/><line x1="6" y1="11" x2="18" y2="11"/><line x1="12" y1="3" x2="12" y2="11"/><circle cx="12" cy="7" r="1" fill="${color}" stroke="none"/></svg>`
    return `data:image/svg+xml,${svg}`
  }

  async _loadDocument(data) {
    const { Core, UI } = this._instance

    // Fetch PDF as blob from parent page to avoid CORS issues
    // (WebViewer's iframe can't fetch cross-origin S3 URLs directly)
    const pdfResponse = await fetch(data.pdf_url)
    const pdfBlob = await pdfResponse.blob()
    UI.loadDocument(pdfBlob, { filename: data.name || "document.pdf", extension: "pdf" })

    // Wait for the document to fully load
    await new Promise((resolve, reject) => {
      const onLoaded = () => {
        Core.documentViewer.removeEventListener("documentLoaded", onLoaded)
        Core.documentViewer.removeEventListener("loaderror", onError)
        resolve()
      }
      const onError = (err) => {
        Core.documentViewer.removeEventListener("documentLoaded", onLoaded)
        Core.documentViewer.removeEventListener("loaderror", onError)
        console.error("[pdf-viewer] Document load error:", err?.message || err)
        reject(err)
      }
      Core.documentViewer.addEventListener("documentLoaded", onLoaded)
      Core.documentViewer.addEventListener("loaderror", onError)
    })

    const annotManager = Core.documentViewer.getAnnotationManager()
    const docViewer = Core.documentViewer

    // Read-only mode
    annotManager.enableReadOnlyMode()

    // Import annotations (XFDF). Strip <apref> appearance pointers first —
    // a stale one wedges page paint in an endless retry and the sheet stays
    // white (see action_item_pdf_viewer_controller._stripBrokenAppearanceRefs).
    if (data.xfdf) {
      const cleaned = data.xfdf
        .replace(/<apref\b[^>]*\/>/g, "")
        .replace(/<apref\b[^>]*>[\s\S]*?<\/apref>/g, "")
      await annotManager.importAnnotations(cleaned)
    }

    // Hide noisy "AutoCAD SHX Text" comments from the panel by default
    this._applyDefaultCommentFilter()

    // Apply page rotations
    if (data.page_rotations) {
      const rotations = typeof data.page_rotations === "string"
        ? JSON.parse(data.page_rotations)
        : data.page_rotations

      const totalPages = docViewer.getPageCount()
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const rotation = rotations[pageNum]
        if (typeof rotation === "number" && [0, 1, 2, 3].includes(rotation)) {
          try {
            docViewer.setRotation(rotation, pageNum)
          } catch (e) {
            console.error(`Error setting rotation for page ${pageNum}:`, e)
          }
        }
      }
    }

    UI.setToolMode("Pan")

    // Default layout: fit-to-page, notes panel open (left panel closed)
    UI.setFitMode(UI.FitMode.FitPage)
    UI.openElements(["notesPanel"])
    UI.setNotesPanelWidth(280)

    this.loadingTarget.classList.add("hidden")
    this._pdfData = data
  }

  // AutoCAD-exported PDFs embed SHX text labels as annotations authored by
  // "AutoCAD SHX Text", which flood the comments panel. Hide that author from
  // the notes/comments panel by default. This only filters the comments list —
  // the drawing itself is untouched.
  _applyDefaultCommentFilter() {
    const HIDDEN_AUTHOR = "AutoCAD SHX Text"
    try {
      this._instance.UI.setCustomNoteFilter((annot) => (annot.Author || "") !== HIDDEN_AUTHOR)
    } catch (e) {
      console.warn("[pdf-viewer] default comment filter failed:", e)
    }
  }

  async download() {
    if (!this._instance || !this._pdfData) return

    const { UI } = this._instance
    const filename = this._pdfData.name || "document"

    // Use WebViewer's built-in download which includes annotations
    UI.downloadPdf({ filename })
  }

  disconnect() {
    document.removeEventListener("click", this._onGlobalClick)
    if (this._instance) {
      this._instance.UI.dispose()
      this._instance = null
    }
    this._currentDevPdfId = null
  }
}
